import { useAppSearchParams } from 'shared/libs/hooks/useAppSearchParams'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface Props {
  pages?: string[]
  interval?: number
}

export const usePagesUpdate = ({ pages, interval }: Props) => {
  const { getAllParams } = useAppSearchParams()
  const searchParams = useMemo(() => getAllParams(), [getAllParams])
  const [updateIsValid, setUpdateIsValid] = useState<Record<string, boolean>>(
    {},
  )
  const timeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const clearAllTimeouts = useCallback(() => {
    Object.values(timeoutsRef.current).forEach(clearTimeout)
    timeoutsRef.current = {}
  }, [])

  const refreshPageValidity = useCallback((page: string) => {
    setUpdateIsValid((prev) => {
      if (prev[page] === true) return prev
      return { ...prev, [page]: true }
    })

    if (timeoutsRef.current[page]) {
      clearTimeout(timeoutsRef.current[page])
    }
    if (interval) {
      timeoutsRef.current[page] = setTimeout(() => {
        setUpdateIsValid((current) => {
          if (current[page] === false) return current
          return { ...current, [page]: false }
        })
        delete timeoutsRef.current[page]
      }, interval)
    }
  }, [])

  useEffect(() => {
    if (!pages) return

    setUpdateIsValid((prev) => {
      const newState = { ...prev }
      let hasChanges = false

      pages.forEach((page) => {
        if (!(page in newState)) {
          newState[page] = true
          hasChanges = true

          if (timeoutsRef.current[page]) {
            clearTimeout(timeoutsRef.current[page])
          }
          if (interval) {
            timeoutsRef.current[page] = setTimeout(() => {
              setUpdateIsValid((current) => {
                if (current[page] === false) return current
                return { ...current, [page]: false }
              })
              delete timeoutsRef.current[page]
            }, interval)
          }
        }
      })

      return hasChanges ? newState : prev
    })
  }, [pages])

  const resetPagesRelevance = useCallback(() => {
    setUpdateIsValid((prev) => {
      const newState = Object.keys(prev).reduce(
        (acc, key) => {
          acc[key] = false
          return acc
        },
        {} as Record<string, boolean>,
      )

      return newState
    })
    clearAllTimeouts()
  }, [])

  useEffect(() => {
    clearAllTimeouts()
    setUpdateIsValid({})
  }, [searchParams])

  return {
    updateIsValid,
    refreshPageValidity,
    resetPagesRelevance,
  }
}
